import { useEffect, useState } from "react";

import { Icon_Comment24, isLoggedIn, Spinner } from "@shapr3d/frontend-lib";
import classNames from "classnames";

import { type CommentID, useComments } from "../../hooks/useComments";
import { useCommentVersion } from "../../hooks/useCommentVersion";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { useWebExport } from "../../hooks/useWebExport";
import { useWindowSize, ViewportType } from "../../hooks/useWindowSize";
import { useCommentsStore } from "../../stores/commentStore";
import { Comment } from "../Comment/Comment";
import { Sidebar } from "../Sidebar/Sidebar";
import { ToolButton } from "../ToolButton/ToolButton";

import style from "./CommentSidebar.module.css";

interface CommentSidebarProps {
  isOpen: boolean;
  isDisabled?: boolean;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CommentSidebar = ({ isOpen, setIsOpen, isDisabled }: CommentSidebarProps) => {
  const tracker = useTracker();
  const { t, m } = useTranslation();
  const {
    data: commentsData,
    isSuccess: isCommentsSuccess,
    isError: isCommentsFetchError,
    isLoading: isCommentsLoading,
  } = useComments();
  const { isNewerVersion } = useCommentVersion();
  const {
    data: webExportData,
    isSuccess: isWebExportSuccess,
    isLoading: isWebExportLoading,
    viewLinkId,
  } = useWebExport();
  const { setOpenCommentId, openCommentId } = useCommentsStore();
  const { viewportType } = useWindowSize();

  const handleCommentClick = (commentId: CommentID) => {
    tracker.trackCommentSelected({
      commentId,
      webExportId: webExportData?.webExport.webExportId,
      source: "sidebar",
      viewLinkId: viewLinkId ?? "",
    });

    setOpenCommentId(commentId);
  };

  const isLoaded = isCommentsSuccess && isWebExportSuccess;
  const isPrivateCloud = Boolean(webExportData?.isPrivateCloud);
  const isCommentingEnabled = Boolean(webExportData?.isCommentingEnabled);

  // Open the sidebar by default
  const [isFirstFetch, setIsFirstFetch] = useState(true);
  useEffect(() => {
    if (
      isCommentsSuccess &&
      isFirstFetch &&
      viewportType !== ViewportType.Mobile &&
      isCommentingEnabled
    ) {
      tracker.trackCommentingDisplayed({
        viewLinkId: viewLinkId ?? "",
        webExportId: webExportData?.webExport.webExportId,
      });
      setIsFirstFetch(false);
      setIsOpen?.(true);
    }
  }, [
    isCommentsSuccess,
    isFirstFetch,
    setIsOpen,
    setIsFirstFetch,
    tracker,
    viewLinkId,
    viewportType,
    isCommentingEnabled,
    webExportData,
  ]);

  return (
    <Sidebar
      title={t(m.Comments.Title)}
      position="right"
      isOpen={isOpen}
      onClose={() => setIsOpen?.(false)}
      controlButtons={
        !isPrivateCloud ? (
          <ToolButton
            title={t(m.Comments.ToolButtonTitle)}
            alt={t(m.Comments.ToolButtonAlt)}
            icon={<Icon_Comment24 />}
            onClick={() => {
              setIsOpen?.((isOpen) => {
                const newValue = !isOpen;
                tracker.trackCommentingSidebarVisibilityChanged({
                  viewLinkId: viewLinkId ?? "",
                  visible: newValue,
                });
                return newValue;
              });
            }}
            isActive={isOpen}
            isDisabled={isDisabled}
          />
        ) : undefined
      }
    >
      {!isLoggedIn() && webExportData?.baseRole === "viewer" && (
        <div className={style.emptyContent}>
          <div className={style.text}>
            {t(m.Comments.LoginPrompt, {
              br: () => <br />,
            })}
          </div>
        </div>
      )}

      {isCommentsFetchError && (
        <div className={style.emptyContent}>
          <div className={style.text}>{t(m.Comments.LoadError)}</div>
        </div>
      )}

      {(isWebExportLoading || isCommentsLoading) && isLoggedIn() && (
        <div className={style.emptyContent}>
          <Spinner size={20} />
        </div>
      )}

      {isLoaded && !commentsData?.comments?.length && (
        <div className={style.emptyContent}>
          <div className={style.text}>{t(m.Comments.EmptyState)}</div>
        </div>
      )}

      {commentsData?.comments?.map((comment) => {
        return (
          <div
            key={comment.commentId}
            className={style.commentSidebarBox}
            onClick={() => handleCommentClick(comment.commentId)}
          >
            <div
              className={classNames(
                style.commentSidebarCard,
                comment.commentId === openCommentId && style.active
              )}
            >
              <Comment
                isTruncated
                initialMode="view"
                title={commentsData?.users[comment.createdByUserId] ?? t(m.Comments.UnknownUser)}
                date={comment.createdAt}
                isNewerVersion={isNewerVersion(comment)}
                actions={<Comment.ContextMenu comment={comment} />}
              >
                {comment.message}
              </Comment>
            </div>
          </div>
        );
      })}
    </Sidebar>
  );
};
